import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { links, salesMailtoUrl } from "../shared/constants/constants"
import { PRICING_PLANS_CONTENT } from "../../shared/pricingPlansContent"
import AnnaSourcingDemo from "./AnnaSourcingDemo"
import AlexScoutDemo from "./AlexScoutDemo"
import atsJobsListSrc from "./assets/recruiting-screen.webp"
import atsJobDetailsSrc from "./assets/recruiting-job-details-screen.webp"
import styles from "./startups-landing-sections.module.css"


const ATS_SLIDES = [
	{ label: "Jobs pipeline", src: atsJobsListSrc, alt: "Techcore Recruiting — jobs pipeline list view" },
	{ label: "Job details", src: atsJobDetailsSrc, alt: "Techcore Recruiting — job details and description" },
]

const ATS_SLIDE_INTERVAL = 6000

function AtsScreenshotSlider() {
	const [active, setActive] = useState(0)
	const timerRef = useRef(null)

	const goTo = (idx) => {
		setActive(idx)
		clearInterval(timerRef.current)
		timerRef.current = setInterval(() => {
			setActive((prev) => (prev + 1) % ATS_SLIDES.length)
		}, ATS_SLIDE_INTERVAL)
	}

	useEffect(() => {
		timerRef.current = setInterval(() => {
			setActive((prev) => (prev + 1) % ATS_SLIDES.length)
		}, ATS_SLIDE_INTERVAL)
		return () => clearInterval(timerRef.current)
	}, [])

	return (
		<div className={styles.atsSlider}>
			<div className={styles.atsSliderFrame}>
				<div className={styles.atsSliderTrack}>
					{ATS_SLIDES.map((s, i) => (
						<img
							key={i}
							src={s.src}
							alt={s.alt}
							className={`${styles.atsSliderImg} ${i === active ? styles.atsSliderImgActive : ""}`}
							draggable={false}
						/>
					))}
				</div>
			</div>
			<div className={styles.atsSliderDots}>
				{ATS_SLIDES.map((_, i) => (
					<button
						key={i}
						aria-label={`Show slide ${i + 1}`}
						className={`${styles.atsSliderDot} ${i === active ? styles.atsSliderDotActive : ""}`}
						onClick={() => goTo(i)}
					/>
				))}
			</div>
		</div>
	)
}

function FadeUp({ children, className = "", delayClass = "" }) {
	const ref = useRef(null)
	useEffect(() => {
		const el = ref.current
		if (!el) return
		const obs = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) e.target.classList.add(styles.fadeUpVisible)
				})
			},
			{ threshold: 0.12 }
		)
		obs.observe(el)
		return () => obs.disconnect()
	}, [])
	return (
		<div ref={ref} className={`${styles.fadeUp} ${delayClass} ${className}`}>
			{children}
		</div>
	)
}

const StartupsLandingSections = () => {
	return (
		<div className={styles.startupsLandingRoot}>
			<div id="logos" className={styles.logos}>
				<div className={`container ${styles.sectionContainer}`}>
					<div className={styles.logosInner}>
						<span className={styles.logosLabel}>Trusted by teams at</span>
						<div className={styles.logosRow}>
							{["Northform", "Arklio", "Vectis", "Suprima", "Draftly", "Corewave"].map((name) => (
								<span key={name} className={styles.fakeLogo}>
									{name}
								</span>
							))}
						</div>
					</div>
				</div>
			</div>

			<section id="pillars" className={styles.pillars}>
				<div className={`container ${styles.sectionContainer}`}>
					<FadeUp>
						<div className={styles.pillarsGrid}>
							<div className={styles.pillarsCopy}>
								<div className={styles.label}>Platform</div>
								<h2>
									Everything you need to
									<br />
									build and run a great team.
								</h2>
							</div>
							<p className={styles.bodyLg}>
								From first hire to fast-growing team, one platform that scales with you. Whether you're hiring your first employee or managing a distributed team across time zones, our HR platform brings everything together — recruiting, people ops, and a curated network of talent and partners to help you hire, scale, and grow your business the right way.
							</p>
						</div>
					</FadeUp>
				</div>
			</section>

			<section id="ats" className={styles.ats}>
				<div className={`container ${styles.sectionContainer}`}>
					<div className={styles.featureSplit}>
					<FadeUp>
						<AtsScreenshotSlider />
					</FadeUp>

						<FadeUp delayClass={styles.fadeUp2}>
							<div className={styles.featureCopy}>
								<div className={styles.label}>Recruiting & ATS</div>
								<h2>Recruiting that lives where your team does.</h2>
								<p className={styles.body}>
									Hire faster and smarter with one connected flow. From job posting and AI-powered sourcing to candidate evaluation and final decision. Everything you expect from a modern ATS, fully integrated with your HR platform and backed by the Techcore Network.
								</p>
								<ul className={styles.featureChecklist}>
									<li>Flexible pipeline with custom stages, statuses, and email templates</li>
									<li>Granular roles and permissions for your whole hiring team</li>
									<li>Branded careers page with built-in talent pool</li>
									<li>Outbound sequencing to engage candidates across email and LinkedIn</li>
									<li>AI-powered candidate sourcing via Anna, built into every job</li>
								</ul>
								<a href="#pricing" className={styles.btnPrimary}>
									Get started →
								</a>
							</div>
						</FadeUp>
					</div>
				</div>
			</section>

			<section id="ai-sourcing" className={styles.aiSourcing}>
				<div className={styles.aiBg} />
				<div className={`container ${styles.sectionContainer}`}>
					<div className={styles.featureSplit}>
						<FadeUp>
							<div className={styles.featureCopy}>
								<div className={styles.label}>AI Talent Sourcing</div>
								<h2>
									Meet Anna.
									<br />
									She sources talent
									<br />
									<em className={styles.headlineEm}>instantly.</em>
								</h2>
								<p className={styles.body}>
									Describe what you need in plain language. Anna helps you shape the brief, searches the Techcore Network (a curated pool of individual talents and agency professionals), scores every match, and writes detailed feedback on each candidate. Once the right people surface, she handles the initial conversation — answering their questions, asking yours, and moving things forward. The goal is simple: connect the right candidate with the right opportunity.
								</p>
								<ul className={styles.featureChecklist}>
									<li>Drafts the job description before the search begins</li>
									<li>Searches the Techcore Network across individual talents and agency professionals</li>
									<li>Scores every match with detailed written feedback</li>
									<li>Takes care of routine candidate conversations — answering questions and gathering what you need</li>
									<li>Flows shortlisted candidates and all collected notes directly into your ATS pipeline</li>
								</ul>
								<a href="#pricing" className={styles.btnPrimary}>
									Try Anna →
								</a>
							</div>
						</FadeUp>

						<FadeUp delayClass={styles.fadeUp2}>
							<AnnaSourcingDemo />
						</FadeUp>
					</div>
				</div>
			</section>

			<section id="consultants" className={styles.consultants}>
				<div className={`container ${styles.sectionContainer}`}>
					<div className={styles.featureSplit}>
						<FadeUp>
							<AlexScoutDemo />
						</FadeUp>

						<FadeUp delayClass={styles.fadeUp2}>
							<div className={styles.featureCopy}>
								<div className={styles.label}>AI Consultant Discovery</div>
								<h2>Expert help, exactly when your team needs it.</h2>
								<p className={styles.body}>
									Growing teams often need senior expertise they can&apos;t justify hiring full-time. Anna surfaces
									verified consultants from partner agencies — fractional CTOs, M&amp;A advisors, compliance
									specialists, growth leads — with transparent rates, real case studies, and immediate
									availability. Not a freelancer marketplace. Agency-grade people with a track record.
								</p>
								<ul className={styles.featureChecklist}>
									<li>Tell Anna the expertise you need — she finds the match</li>
									<li>Verified profiles with industry verticals and past outcomes</li>
									<li>Engagement types: fractional, project-based, or advisory</li>
									<li>Transparent day rates — no hidden placement fees</li>
									<li>Contract and onboarding handled inside Techcore</li>
									<li>Available on all HRIS plans</li>
								</ul>
								<a href="#pricing" className={styles.btnPrimary}>
									Explore consultants →
								</a>
							</div>
						</FadeUp>
					</div>
				</div>
			</section>

			<section id="network" className={styles.network}>
				<div className={styles.networkBg} />
				<div className={`container ${styles.sectionContainer} ${styles.networkInner}`}>
					<div className={styles.labelCenter}>Techcore Network</div>
					<h2>
						A curated network of
						<br />
						talent, agencies, and experts.
					</h2>
					<p className={styles.networkLead}>
						Behind every sourcing run Anna does is the Techcore Network — individual talents, agency professionals,
						and senior consultants, all in one place and ready to engage.
					</p>
					<FadeUp>
						<div className={styles.statsRow}>
							<div className={styles.statCell}>
								<div className={styles.statVal}>12k+</div>
								<div className={styles.statKey}>Active talents</div>
							</div>
							<div className={styles.statCell}>
								<div className={styles.statVal}>340+</div>
								<div className={styles.statKey}>Partner agencies</div>
							</div>
							<div className={styles.statCell}>
								<div className={styles.statVal}>800+</div>
								<div className={styles.statKey}>Senior consultants</div>
							</div>
							<div className={styles.statCell}>
								<div className={styles.statVal}>4.8d</div>
								<div className={styles.statKey}>Avg. time to first match</div>
							</div>
						</div>
					</FadeUp>
				</div>
			</section>

			<section id="how-it-works" className={styles.howItWorks}>
				<div className={`container ${styles.sectionContainer}`}>
					<FadeUp>
						<div className={styles.howHeader}>
							<div className={styles.labelCenter}>How it works</div>
							<h2>
								From day one to done.
								<br />
								The complete team lifecycle.
							</h2>
						</div>
					</FadeUp>
					<div className={styles.howGrid}>
						<div className={styles.howConnector} aria-hidden />
						<FadeUp>
							<div className={styles.howStep}>
								<div className={`${styles.howCircle} ${styles.howCircleBlue}`}>
									<span>01</span>
								</div>
								<h3>Source instantly</h3>
								<p className={styles.body}>
									Tell Anna what you need. She searches the Techcore Network across individual talents, agency
									professionals, and senior consultants — and returns a ranked shortlist within hours.
								</p>
							</div>
						</FadeUp>
						<FadeUp delayClass={styles.fadeUp2}>
							<div className={styles.howStep}>
								<div className={`${styles.howCircle} ${styles.howCirclePurple}`}>
									<span>02</span>
								</div>
								<h3>Hire without friction</h3>
								<p className={styles.body}>
									Move candidates through a built-in ATS pipeline. Score interviews, send offers, collect
									e-signatures — and watch every approved hire land automatically in your People directory. No
									copy-paste. Ever.
								</p>
							</div>
						</FadeUp>
						<FadeUp delayClass={styles.fadeUp3}>
							<div className={styles.howStep}>
								<div className={`${styles.howCircle} ${styles.howCircleTeal}`}>
									<span>03</span>
								</div>
								<h3>Manage seamlessly</h3>
								<p className={styles.body}>
									Vacations, compensation, performance reviews, team org — all in one place from the start. No new
									setup when your team grows. The platform grows with you.
								</p>
							</div>
						</FadeUp>
					</div>
				</div>
			</section>

			<section id="pricing" className={styles.pricing}>
				<div className={`container ${styles.sectionContainer}`}>
					<FadeUp>
						<div className={styles.pricingHeader}>
							<div className={styles.labelCenter}>Pricing</div>
							<h2>Build the plan that fits your team.</h2>
							<p className={`${styles.bodyLg} ${styles.pricingIntro}`}>
								Every plan includes the full platform — ATS, AI Sourcing, and Consultant Discovery. Tune AI credits
								and add-ons on the detailed pricing page.
							</p>
						</div>
					</FadeUp>
					<div className={styles.pricingGrid}>
						<FadeUp>
							<div className={styles.planCard}>
								<div className={styles.planName}>{PRICING_PLANS_CONTENT.starter.name}</div>
								<div className={styles.planPrice}>
									${PRICING_PLANS_CONTENT.starter.baseMonthly} <span>/ mo</span>
								</div>
								<div className={styles.planSub}>{PRICING_PLANS_CONTENT.starter.sub}</div>
								<div className={styles.planCredit}>
									Includes <strong>1 AI Sourcing credit</strong> every 6 months
								</div>
								<ul className={styles.planFeatures}>
									{PRICING_PLANS_CONTENT.starter.features.map((line) => (
										<li key={line}>
											<span className={styles.check}>✓</span> {line}
										</li>
									))}
								</ul>
								<a href={links.startupsSignUp} className={`${styles.btnGhost} ${styles.planBtn}`}>
									Get started
								</a>
							</div>
						</FadeUp>
						<FadeUp delayClass={styles.fadeUp2}>
							<div className={`${styles.planCard} ${styles.planCardFeatured}`}>
								<span className={styles.planFeaturedBadge}>Most popular</span>
								<div className={styles.planName}>{PRICING_PLANS_CONTENT.growth.name}</div>
								<div className={styles.planPrice}>
									${PRICING_PLANS_CONTENT.growth.baseMonthly} <span>/ mo</span>
								</div>
								<div className={styles.planSub}>{PRICING_PLANS_CONTENT.growth.sub}</div>
								<div className={styles.planCredit}>
									Includes <strong>1 AI Sourcing credit</strong> every 6 months
								</div>
								<ul className={styles.planFeatures}>
									{PRICING_PLANS_CONTENT.growth.features.map((line) => (
										<li key={line}>
											<span className={styles.check}>✓</span> {line}
										</li>
									))}
								</ul>
								<a href={`${links.startupsSignUp}?plan=growth`} className={`${styles.btnPrimary} ${styles.planBtn}`}>
									Start Growth plan →
								</a>
							</div>
						</FadeUp>
					</div>
					<p className={styles.pricingFootnote}>
						Additional AI Sourcing credits available as add-ons. Each credit = one full sourcing run with Anna across
						talents, agencies, and consultants.{" "}
						<Link to="/pricing" className={styles.pricingLink}>
							View detailed startup pricing →
						</Link>
					</p>
				</div>
			</section>

			<section id="cta-band" className={styles.ctaBand}>
				<div className={styles.ctaBandBg} />
				<div className={`container ${styles.sectionContainer} ${styles.ctaBandInner}`}>
					<h2>
						Build your team.
						<br />
						<em className={styles.headlineEm}>Start today.</em>
					</h2>
					<p className={styles.bodyLg}>
						Source from a curated network. Hire without friction.
						<br />
						Manage everything in one place.
					</p>
					<div className={styles.ctaActions}>
						<a href={links.startupsSignUp} className={styles.btnPrimary}>
							Get started →
						</a>
						<a href={salesMailtoUrl} className={styles.btnGhost}>
							Talk to sales
						</a>
					</div>
					<p className={styles.ctaNote}>Questions? hello@techcore.io · Response within 1 business day</p>
				</div>
			</section>
		</div>
	)
}

export default StartupsLandingSections
